import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { typography } from '../../tokens';

export type NumberTagVariant = 'black' | 'white' | 'success' | 'neutral' | 'warning' | 'error' | 'info';

export interface NumberTagProps {
  /**
   * The number to display (1-99)
   */
  number: number;

  /**
   * The color variant
   */
  variant?: NumberTagVariant;

  /**
   * Test ID for testing
   */
  testID?: string;
}

const variantStyles: Record<NumberTagVariant, { backgroundColor: string; textColor: string }> = {
  black: {
    backgroundColor: '#020202',
    textColor: '#FCFCFC',
  },
  white: {
    backgroundColor: '#FCFCFC',
    textColor: '#020202',
  },
  success: {
    backgroundColor: '#D3F4E7',
    textColor: '#24835D',
  },
  neutral: {
    backgroundColor: '#ECEEF1',
    textColor: '#4F5667',
  },
  warning: {
    backgroundColor: '#FFF4C1',
    textColor: '#D7A321',
  },
  error: {
    backgroundColor: '#FFD9D2',
    textColor: '#E24D2A',
  },
  info: {
    backgroundColor: '#D0E5F9',
    textColor: '#4A90E2',
  },
};

export const NumberTag: React.FC<NumberTagProps> = ({
  number,
  variant = 'black',
  testID,
}) => {
  const { backgroundColor, textColor } = variantStyles[variant];
  const displayNumber = number > 99 ? '99+' : number.toString();

  // Determine if we need pill shape (for multi-digit numbers)
  const needsPillShape = displayNumber.length > 1;

  return (
    <View
      style={[
        styles.container,
        { backgroundColor },
        needsPillShape && styles.pillContainer,
      ]}
      testID={testID}
      accessibilityRole="text"
      accessibilityLabel={`${number} notifications`}
    >
      <Text style={[styles.text, { color: textColor }]}>{displayNumber}</Text>
    </View>
  );
};

NumberTag.displayName = 'NumberTag';

const styles = StyleSheet.create({
  container: {
    minWidth: 16,
    height: 16,
    borderRadius: 99, // Full circle/pill
    alignItems: 'center',
    justifyContent: 'center',
  },
  pillContainer: {
    paddingHorizontal: 4,
  },
  text: {
    ...typography.label.small,
    fontWeight: '600',
  },
});
